
import React, { useState, useRef, useEffect } from 'react';
import { Download, ChevronDown } from 'lucide-react';
import { Button } from './Button';

interface DownloadOption {
    label: string;
    url: string;
    icon?: React.ReactNode;
}

interface DownloadDropdownProps {
    options: DownloadOption[];
    buttonText: string;
    variant?: 'primary' | 'outline';
    className?: string;
    forceDirection?: 'down' | 'lateral'; // Forza direzione specifica
    forceNoMargin?: boolean; // Rimuove margine laterale
}

export const DownloadDropdown: React.FC<DownloadDropdownProps> = ({
    options,
    buttonText,
    variant = 'outline',
    className = '',
    forceDirection,
    forceNoMargin = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [openUpward, setOpenUpward] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen && buttonRef.current) {
            // Su mobile O se forceDirection è 'down', forza SEMPRE verso il basso
            const isMobile = window.innerWidth < 768;
            if (isMobile || forceDirection === 'down') {
                setOpenUpward(false);
                return;
            }

            const rect = buttonRef.current.getBoundingClientRect();
            const spaceRight = window.innerWidth - rect.right;
            const spaceLeft = rect.left;

            // Se c'è poco spazio a destra (meno di 250px), apri a sinistra
            if (spaceRight < 250 && spaceLeft > spaceRight) {
                setOpenUpward(true); // Riutilizzo per "sinistra"
            } else {
                setOpenUpward(false); // "destra"
            }
        }
    }, [isOpen, forceDirection]);

    return (
        <div className={`relative ${isOpen ? 'z-[10000]' : ''} ${className}`} ref={dropdownRef}>
            <Button
                ref={buttonRef}
                variant={variant}
                onClick={() => setIsOpen(!isOpen)}
                className="border-gray-700 text-white hover:bg-gray-800 font-bold py-4 px-8 rounded-xl flex items-center gap-2"
            >
                <Download className="text-blue-400" size={20} />
                {buttonText}
                <ChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                />
            </Button>

            {isOpen && (
                <div className={`absolute min-w-[160px] max-w-[90vw] bg-[#151E32] backdrop-blur-md border border-gray-700 rounded-xl shadow-2xl overflow-hidden z-[9999] animate-in fade-in duration-200 transition-all ${forceDirection === 'down'
                    ? 'top-full mt-2 left-1/2 -translate-x-1/2 slide-in-from-top-2'
                    : openUpward
                        ? `top-0 right-full ${forceNoMargin ? '' : 'mr-2'} slide-in-from-right-2`
                        : `top-0 left-full ${forceNoMargin ? '' : 'ml-2'} slide-in-from-left-2`
                    }`}>
                    {options.map((option, index) => (
                        <a
                            key={index}
                            href={option.url}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setIsOpen(false)}
                            className="flex items-center gap-3 px-4 py-3 hover:bg-gray-800 transition-colors text-white text-sm border-b border-gray-800 last:border-0"
                        >
                            {option.icon || <Download size={16} className="text-gray-400" />}
                            <span>{option.label}</span>
                        </a>
                    ))}
                </div>
            )}
        </div>
    );
};
